import React, { useContext, useMemo, useState } from "react";
import MunicipioContext from "../../context/MunicipiosContext";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearch } from "@fortawesome/free-solid-svg-icons";

import Page from "../templates/page";
import Cargando from "../components/cargando";
import useTituloPagina from "../../utils/useTituloPagina";

//Etiquetas públicas de la etapa. Son distintas de las de la consola: al vecino
//no le dice nada "informativa", le dice si puede hacer algo hoy.
const ETAPA = {
  informativa: { texto: "Información", destacada: false },
  presentacion: { texto: "Presentá tu propuesta", destacada: true },
  votacion: { texto: "Votación abierta", destacada: true },
  seleccion: { texto: "Proyectos elegidos", destacada: false },
};

const VIGENTES = "vigentes";

//Primer año con ediciones en la plataforma. Desde acá hasta el año en curso se
//arma el selector, aunque algún año no tenga municipios cargados todavía: así
//el desplegable no cambia de largo entre visitas y no hay que tocar el código
//cada primero de enero.
const PRIMER_ANIO = 2020;

//El año está en la URL del municipio (chazon2024) y no siempre en el nombre:
//hay quince cargados como "Ballesteros" a secas. Por eso se lee de la URL, y
//el nombre queda solo para mostrar.
const anioDe = (municipio) => {
  const m = String(municipio.nombreUrl || "").match(/(20\d{2})/);
  return m ? Number(m[1]) : null;
};

//Nombre de la localidad, sin el año ni lo que venga después. Sirve para contar
//municipios: Colonia Caroya es uno solo aunque corra tres presupuestos.
const localidadDe = (municipio) =>
  String(municipio.nombreUrl || "").replace(/(20\d{2}).*$/, "");

//Identifica un PROCESO: la localidad más el segmento que va después del año.
//
//Un municipio puede correr varios presupuestos en paralelo el mismo año.
//Colonia Caroya tiene tres —coloniacaroya2026ciudadano, ...juvenil y
//...personasmayores— y son tres procesos distintos, cada uno con sus
//propuestas y su votación, no tres ediciones del mismo. Agrupando solo por
//localidad, "última edición" mostraba uno y escondía los otros dos.
const procesoDe = (municipio) => {
  const url = String(municipio.nombreUrl || "");
  const partes = url.match(/^(.*?)(20\d{2})(.*)$/);
  //Sin año en la URL no hay nada que separar: el proceso es la URL entera.
  return partes ? `${partes[1]}|${partes[3]}` : url;
};

//Compara sin acentos ni mayúsculas: quien busca "cordoba" tiene que encontrar
//"Córdoba", y poner la tilde en un teclado de celular es un paso de más.
const plegar = (t) =>
  String(t || "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .trim();

const Home = () => {
  const { municipios } = useContext(MunicipioContext);
  const [busqueda, guardarBusqueda] = useState("");
  const [anio, guardarAnio] = useState(VIGENTES);
  useTituloPagina("Municipios participantes");

  //Municipios que se publican.
  //
  //Se saca el demo: es un municipio de prueba, y en la página pública tanto
  //listarlo como contarlo entre los participantes dice algo que no es cierto.
  //Sigue accesible por su dirección, /municipio/demo.
  const reales = useMemo(
    () => municipios.filter((m) => localidadDe(m) !== "demo"),
    [municipios]
  );

  //Números de la presentación.
  const resumen = useMemo(() => {
    const localidades = [];
    reales.forEach((m) => {
      const l = localidadDe(m);
      if (l && localidades.indexOf(l) === -1) localidades.push(l);
    });
    return { municipios: localidades.length, ediciones: reales.length };
  }, [reales]);

  //Años del selector: de PRIMER_ANIO hasta el actual, del más nuevo al más
  //viejo. Se toma también el año más alto que haya en los datos, por si se
  //carga una edición del año que viene antes de que empiece.
  const anios = useMemo(() => {
    let ultimo = new Date().getFullYear();
    reales.forEach((m) => {
      const a = anioDe(m);
      if (a && a > ultimo) ultimo = a;
    });
    const lista = [];
    for (let a = ultimo; a >= PRIMER_ANIO; a--) lista.push(a);
    return lista;
  }, [reales]);

  //Qué tarjetas se muestran.
  //
  //Por defecto, solo la edición MÁS RECIENTE de cada municipio: mostrar los 47
  //procesos juntos, mezclando los de 2020 con los de este año, es una pared en
  //la que nadie encuentra el suyo. Las anteriores están a un clic, en las
  //pestañas de año.
  const visibles = useMemo(() => {
    const texto = plegar(busqueda);
    let lista = reales;

    if (anio === VIGENTES) {
      //Se agrupa por PROCESO, no por localidad: si un municipio corre varios
      //presupuestos en paralelo, tienen que aparecer todos.
      const masReciente = {};
      reales.forEach((m) => {
        const clave = procesoDe(m);
        const actual = masReciente[clave];
        if (!actual || (anioDe(m) || 0) > (anioDe(actual) || 0)) {
          masReciente[clave] = m;
        }
      });
      lista = Object.keys(masReciente).map((k) => masReciente[k]);
    } else {
      lista = reales.filter((m) => anioDe(m) === anio);
    }

    if (texto) lista = lista.filter((m) => plegar(m.nombre).indexOf(texto) !== -1);

    //Más nuevos primero y, dentro del mismo año, por nombre.
    return lista.slice().sort((a, b) => {
      const dif = (anioDe(b) || 0) - (anioDe(a) || 0);
      return dif !== 0 ? dif : a.nombre.localeCompare(b.nombre, "es");
    });
  }, [reales, busqueda, anio]);

  if (municipios.length === 0) return <Cargando />;

  return (
    <Page ancho="amplio">
      <h1 className="sr-only">Presupuesto Participativo Raíz</h1>

      {/* ------------------------- PRESENTACIÓN ------------------------- */}
      <div className="home-intro">
        <img
          className="home-logo"
          src="/img/logos/logo_nombre_ppr.svg"
          alt="Presupuesto Participativo Raíz"
        />
        <p className="home-intro-texto">
          Una plataforma digital para gestionar integralmente el Presupuesto
          Participativo, desde la presentación de propuestas por parte de la
          comunidad hasta su votación y selección.
        </p>

        {/* Los números salen de la misma lista de municipios que ya está
            cargada: no hay una consulta extra ni un valor escrito a mano que
            después quede viejo. */}
        <dl className="home-numeros">
          <div className="home-numero">
            <dt>municipios</dt>
            <dd>{resumen.municipios}</dd>
          </div>
          <div className="home-numero">
            <dt>
              {resumen.ediciones === 1
                ? "edición realizada"
                : "ediciones realizadas"}
            </dt>
            <dd>{resumen.ediciones}</dd>
          </div>
        </dl>
      </div>

      {/* -------------------------- DIRECTORIO -------------------------- */}
      <h2 className="ppr-titulo centrado my-3 h4" id="municipios">
        Municipios participantes
      </h2>

      <div className="row">
        <div className="col-12 col-md-7 mb-3">
          <label htmlFor="buscar" className="sr-only">
            Buscar municipio por nombre
          </label>
          <div className="buscador">
            <FontAwesomeIcon
              icon={faSearch}
              className="buscador-lupa"
              aria-hidden="true"
            />
            <input
              id="buscar"
              type="search"
              className="form-control"
              placeholder="Buscar tu municipio…"
              value={busqueda}
              onChange={(e) => guardarBusqueda(e.target.value)}
            />
          </div>
        </div>

        <div className="col-12 col-md-5 mb-3">
          <label htmlFor="anio" className="sr-only">
            Filtrar por año
          </label>
          <select
            id="anio"
            className="custom-select"
            value={anio}
            onChange={(e) =>
              guardarAnio(
                e.target.value === VIGENTES ? VIGENTES : Number(e.target.value)
              )
            }
          >
            <option value={VIGENTES}>Última edición de cada municipio</option>
            {anios.map((a) => (
              <option key={a} value={a}>
                Año {a}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* El aviso distingue por qué no hay nada: no es lo mismo que la búsqueda
          no dé resultados que que ese año todavía no tenga municipios. */}
      <p className="text-muted small mb-3" role="status">
        {/* "presupuestos" y no "municipios": cada tarjeta es un proceso, y un
            municipio puede tener varios en paralelo (Colonia Caroya corre tres
            el mismo año). Decir "3 municipios" ahí sería falso. */}
        {visibles.length > 0
          ? `${visibles.length} ${
              visibles.length === 1 ? "presupuesto" : "presupuestos"
            }`
          : busqueda.trim()
            ? `No encontramos municipios con ese nombre${
                anio === VIGENTES ? "" : ` en ${anio}`
              }.`
            : `Todavía no hay presupuestos con una edición ${anio}.`}
      </p>

      <div className="row">
        {visibles.map((municipio) => {
          const etapa = ETAPA[municipio.etapa] || ETAPA.informativa;
          const anioMun = anioDe(municipio);
          return (
            <div className="col-12 col-sm-6 col-lg-4 mb-4" key={municipio._id}>
              <div className="card h-100 tarjeta-municipio">
                {/* La imagen es decorativa (alt vacío): el nombre del municipio
                    ya está en el título, repetirlo en el alt hace que el lector
                    de pantalla lo lea dos veces.
                    Si el archivo no está en el servidor se esconde y queda el
                    fondo celeste, en vez del ícono de imagen rota. */}
                <div className="tarjeta-imagen">
                  {municipio.img && (
                    <img
                      src={`/img/mun_img/${municipio.img}`}
                      alt=""
                      loading="lazy"
                      onError={(e) => {
                        e.target.style.display = "none";
                      }}
                    />
                  )}
                  <span
                    className={`badge tarjeta-etapa ${
                      etapa.destacada ? "badge-success" : "badge-light"
                    }`}
                  >
                    {etapa.texto}
                  </span>
                </div>
                <div className="card-body d-flex flex-column">
                  <h3 className="h6 font-weight-bold mb-1">
                    {municipio.nombre}
                  </h3>
                  {anioMun && (
                    <p className="text-muted small mb-3">Edición {anioMun}</p>
                  )}
                  {/* mt-auto empuja el botón al fondo: con nombres de distinto
                      largo, si no, cada tarjeta lo deja a una altura distinta. */}
                  <Link
                    to={`/municipio/${municipio.nombreUrl}`}
                    className="btn ppr-btn-celeste font-weight-bold mt-auto"
                  >
                    Ver el presupuesto
                  </Link>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* --------------------------- CONTACTO ---------------------------
          Sin el título azul ni tarjeta: es un cierre de página, no una
          sección al mismo nivel que el directorio. El h2 se conserva, en
          tamaño chico, para que el lector de pantalla siga teniendo el índice
          de la página completo. */}
      <section className="home-contacto" aria-labelledby="titulo-contacto">
        <h2 id="titulo-contacto" className="home-contacto-titulo">
          Contacto
        </h2>
        <p className="mb-1">
          ¿Tu municipio quiere implementar el presupuesto participativo, o tenés
          dudas sobre la plataforma? Escribinos a{" "}
          <a className="ppr-a" href="mailto:gestionlocal@extension.unc.edu.ar">
            gestionlocal@extension.unc.edu.ar
          </a>
        </p>
        <p className="mb-0">
          Si tu consulta es sobre una propuesta o una votación en curso,
          escribile directamente a tu municipio: los datos de contacto están en
          su página.
        </p>
      </section>
    </Page>
  );
};

export default Home;
